











import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { FormControl } from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import crossmap from "../../../assets/crossmap.svg";
import { useSelector } from "react-redux";
import LocationPermissionModal from "../../../components/PermissionModal";
import { setProfileDraft } from "./ProfileDraft";
import { Country, State, City } from "country-state-city";
import SearchableDropdown from "../../../components/SearchableComponent";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import {
  getPositionWithFallback,
  useLocationPermissionSync,
} from "../../../utils/getPositionWithFallback";

const defaultCenter = { lat: 37.0902, lng: -95.7129 };  // USA center

const circularRadius = 100;

// ISO → Continent helper
const iso2ToContinent = (iso2) => {
  const m = (iso2 || "").toUpperCase();
  const NA = ["US", "CA", "MX"];
  const SA = ["BR", "AR", "CL", "CO", "PE", "UY", "PY", "BO", "VE", "EC"];
  const EU = ["GB", "FR", "DE", "ES", "IT", "NL", "SE", "NO", "FI", "PL", "PT", "IE", "AT", "GR", "CZ"];
  const AS = ["PK", "IN", "CN", "JP", "AE", "SA", "SG", "TH", "ID", "MY", "BD", "PH", "KR"];
  const AF = ["ZA", "NG", "EG", "KE", "MA", "TZ", "UG", "DZ"];
  const OC = ["AU", "NZ", "FJ", "PG"];
  if (NA.includes(m)) return "North America";
  if (SA.includes(m)) return "South America";
  if (EU.includes(m)) return "Europe";
  if (AS.includes(m)) return "Asia";
  if (AF.includes(m)) return "Africa";
  if (OC.includes(m)) return "Oceania";
  return "Unknown";
};

const Location = ({ onChange, initialLat, initialLng, useBackendLocation = false }) => {
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [position, setPosition] = useState(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : defaultCenter
  );
  const isOnline = () => navigator.onLine;
  const [address, setAddress] = useState("Fetching address...");
  const [displayAddress, setDisplayAddress] = useState("Fetching address...");
  const [showFullMap, setShowFullMap] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isContinueClicked, setIsContinueClicked] = useState(false);
const [countrySearch, setCountrySearch] = useState("");
const [stateSearch, setStateSearch] = useState("");
const [citySearch, setCitySearch] = useState("");




  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addressRef = useRef(null);

  const profileUpdateTokenFromRedux = useSelector((s) => s.auth.profileUpdateToken);
  const profileUpdateToken =
    profileUpdateTokenFromRedux || localStorage.getItem("profileUpdateToken");

  const [locationFields, setLocationFields] = useState({
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    countryCode: "",
    region: "",
    continent: "",
    timezone: "",
  });

  // Fetch address from Nominatim
  const fetchAddress = async ({ lat, lng }) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await res.json();
      const formatted = data?.display_name || "Address not found";
      setAddress(formatted);
      setDisplayAddress(formatted);
      if (addressRef.current) addressRef.current.value = formatted;

      // pick only road/house/building for street_address
      const streetOnly =
        data?.address?.road ||
        data?.address?.neighbourhood ||
        data?.address?.suburb ||
        data?.address?.hamlet ||
        "";

      const iso2 = (data?.address?.country_code || "").toUpperCase();
      setLocationFields({
        latitude: lat,
        longitude: lng,
        address: formatted,
        street: streetOnly,          // 👈 keep raw street part
        city: data?.address?.city || data?.address?.town || data?.address?.village || "",
        province: data?.address?.state || "",
        postalCode: data?.address?.postcode || "",
        country: data?.address?.country || "",
        countryCode: iso2,
        region: iso2ToContinent(iso2),
        continent: iso2ToContinent(iso2),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      });

      // 🔁 Sync map address → dropdowns
const countryName = data?.address?.country || "";
const stateName =
  data?.address?.state || data?.address?.region || "";
const cityName =
  data?.address?.city ||
  data?.address?.town ||
  data?.address?.village ||
  "";

if (countryName) {
  const countryObj = Country.getAllCountries().find(
    (c) => c.name === countryName
  );

  if (countryObj) {
    setSelectedCountry(countryObj.name);
    setSelectedCountryIso(countryObj.isoCode);

    const stateList = State.getStatesOfCountry(countryObj.isoCode).map(
      (s) => ({ name: s.name, isoCode: s.isoCode })
    );
    setStatesList(stateList);

    const stateObj = stateList.find((s) => s.name === stateName);
    if (stateObj) {
      setSelectedState(stateObj.name);
      setSelectedStateIso(stateObj.isoCode);

      const cityList = City.getCitiesOfState(
        countryObj.isoCode,
        stateObj.isoCode
      ).map((c) => c.name);
      setCitiesList(cityList);

      if (cityList.includes(cityName)) {
        setSelectedCity(cityName);
      }
    }
  }
}

    } catch {
      setAddress("Address not found");
      if (addressRef.current) addressRef.current.value = "Address not found";
    }
  };

  const updatePosition = (coords) => {
    setPosition(coords);
    fetchAddress(coords);
    // Call onChange callback if provided
    if (onChange) {
      onChange({
        latitude: coords.lat,
        longitude: coords.lng,
        address: address,
        city: locationFields.city,
        province: locationFields.province,
        country: locationFields.country,
        streetAddress: locationFields.street,
        community: locationFields.community,
        postalCode: locationFields.postalCode,
        timezone: locationFields.timezone,
        countryCode: locationFields.countryCode,
        region: locationFields.region,
        continent: locationFields.continent,
      });
    }
  };

  // Handle position updates when initial coordinates change
  useEffect(() => {
    if (initialLat && initialLng) {
      const coords = { lat: initialLat, lng: initialLng };
      setPosition(coords);
      // Also fetch address for the initial coordinates
      fetchAddress(coords);
    }
  }, [initialLat, initialLng]);

  // Manual address toggle now stays on this page
  const handleManualToggle = (e) => {
    const checked = e.target.checked;
    setManualAddress(checked);
    // Clear the address field when switching to manual mode
    if (checked) {
      setDisplayAddress("");
      setAddress("");
    }
  };

  // Continue without backend save; persist to draft and navigate
  const handleContinue = async () => {
    setIsContinueClicked(true);
    setSaving(true);
    setProfileDraft({
      latitude: locationFields.latitude,
      longitude: locationFields.longitude,
      country: locationFields.country,
      community: "",
      province: locationFields.province,
      city: locationFields.city,
      postal_code: locationFields.postalCode,
      street_address: locationFields.street || "",
      region_code: locationFields.countryCode,
    });
    setTimeout(() => {
      navigate("/region");
      setSaving(false);
    }, 500);
  };

  const handleSearch = async () => {
    if (!searchQuery) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}&limit=1`
      );
      const data = await res.json();
      if (data.length > 0) {
        const { lat, lon } = data[0];
        updatePosition({ lat: parseFloat(lat), lng: parseFloat(lon) });
      } else {
        toast.error("No results found");
      }
    } catch {
      toast.error("Search failed");
    }
  };

  // Country/State/City cascading dropdowns data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
const [countriesData, setCountriesData] = useState([]);
const [statesList, setStatesList] = useState([]);
const [citiesList, setCitiesList] = useState([]);

const [selectedCountry, setSelectedCountry] = useState("");
const [selectedCountryIso, setSelectedCountryIso] = useState("");

const [selectedState, setSelectedState] = useState("");
const [selectedStateIso, setSelectedStateIso] = useState("");

const [selectedCity, setSelectedCity] = useState("");

useEffect(() => {
  const list = Country.getAllCountries().map((c) => ({
    name: c.name,
    isoCode: c.isoCode,
  }));
  setCountriesData(list);
}, []);

  

const handleCountryChange = (value) => {
  setSelectedCountry(value);
  setSelectedState("");
  setSelectedCity("");
  setLoadingStates(true);   // start loading

  const obj = countriesData.find((c) => c.name === value);

  if (obj) {
    setSelectedCountryIso(obj.isoCode);

    const stateList = State.getStatesOfCountry(obj.isoCode).map((s) => ({
      name: s.name,
      isoCode: s.isoCode,
    }));

    setTimeout(() => {
      setStatesList(stateList);
      setLoadingStates(false);   // stop loading
    }, 300);
  }

  setLocationFields(prev => ({
    ...prev,
    country: value,
  }));
};

const handleStateChange = (value) => {
  setSelectedState(value);
  setSelectedCity("");
setLoadingCities(true); 
  setLocationFields(prev => ({
    ...prev,
    province: value,
  }));

  const obj = statesList.find(s => s.name === value);

  if (obj) {
    setSelectedStateIso(obj.isoCode);

    const cityList = City.getCitiesOfState(
      selectedCountryIso,
      obj.isoCode
    ).map((c) => c.name);

    setTimeout(() => {
      setCitiesList(cityList);
      setLoadingCities(false);  // STOP loader
    }, 300);

  }
};


  const geocodeSelection = async (country, state, city) => {
    try {
      const q = [city, state, country].filter(Boolean).join(", ");
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`,
        { headers: { "User-Agent": "yourapp/1.0 (contact@example.com)" } }
      );
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        setPosition(coords);
        fetchAddress(coords);
      } else {
        toast.error("Location not found for selection");
      }
    } catch {
      toast.error("Error locating selection");
    }
  };

const handleCityChange = async (value) => {
  setSelectedCity(value);

  setLocationFields(prev => ({
    ...prev,
    city: value,
  }));

  await geocodeSelection(selectedCountry, selectedState, value);
};


const handleGeolocate = () => {
  if (!navigator.geolocation) {
    toast.error("Geolocation is not supported by your browser");
    return;
  }

if (!navigator.onLine) {
toast.error(t("locationInternetError"));
  return;
}

  getPositionWithFallback(
    (pos) => {
      const coords = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      };
      setPosition(coords);
      fetchAddress(coords);
    },
    (error) => {
      if (error.code === error.PERMISSION_DENIED) {
        setShowPermissionModal(true);
      } else {
        toast.error(t("locationFetchFailed"));
      }
    }
  );
};

useLocationPermissionSync({
  onGranted: () => {
    // Only refetch if the modal was showing, so a manually placed pin is not moved
    if (showPermissionModal) {
      setShowPermissionModal(false);
      handleGeolocate();
    }
  },
  onDenied: () => setShowPermissionModal(true),
});

useEffect(() => {
  const handleOnline = () => {
toast.success(t("internetRestored"));
    // Try fetching address again if we already have coordinates
    if (position?.lat && position?.lng) {
      fetchAddress(position);
    }
  };

  window.addEventListener("online", handleOnline);

  return () => {
    window.removeEventListener("online", handleOnline);
  };
}, [position]);

  useEffect(() => {
    // Single entry point for GPS; skip when coordinates were supplied
    if (!manualAddress && !(initialLat && initialLng) && !useBackendLocation) {
      handleGeolocate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualAddress]);
const filteredCountries = countriesData.filter((c) =>
  c.name.toLowerCase().includes(countrySearch.toLowerCase())
);

const filteredStates = statesList.filter((s) =>
  s.name.toLowerCase().includes(stateSearch.toLowerCase())
);

const filteredCities = citiesList.filter((c) =>
  c.toLowerCase().includes(citySearch.toLowerCase())
);
const [countryOpen, setCountryOpen] = useState(false);
const dropdownLocked = true; 
  return (
      <Box sx={{ py: isMobile ? 5 : 8, px: 2 }}>
        <BaseCard maxWidth={isMobile ? "100%" : "70%"}>
          <CustomText customStyle={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
            {t("pinLocationTitle")}
          </CustomText>
          <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 0.7 }}>
            {t("pinLocationSubtitle")}
          </CustomText>

     
     
              <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center",gap:1, }}>
            <input
              type="checkbox"
              id="manualAddress"
              checked={manualAddress}
              onChange={handleManualToggle}
              style={{ width: 18, height: 18 }}
            />
            <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
              {t("manualAddress")}
            </CustomText>
          </Box>
        </Box>

          {/* Actions - only when not manual address */}
          {!manualAddress && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 2 : 0,
              justifyContent: "space-between",
              alignItems: "center",
              my: 2,
            }}
          >
            <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
              {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
            </CustomText>
            <Box sx={{ display: "flex", gap: 1 }}>
            
              <IconButton
                onClick={handleGeolocate}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  borderRadius: 1,
                  width: 40,
                  height: 31,
                  px: 2,
                  "&:hover": { backgroundColor: "#1976d2" },
                }}
              >
                <RoomIcon fontSize="small" />
              </IconButton>
              <CustomButton
                label={t("fullMap")}
                size="small"
                sx={{
                  height: 31,
                  minWidth: "unset",
                  px: 2,
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  fontSize: 12,
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => setShowFullMap(true)}
              />
            </Box>
          </Box>
          )}

          {/* 🔍 Search Bar - only when not manual address */}
          {!manualAddress && searchBarOpen && (
            <Box sx={{ mb: 2 }}>
              <input
                type="text"
                placeholder="Search location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "15px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  marginBottom: "12px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  fontFamily: "Poppins, sans-serif",
                }}
              />
            </Box>
          )}


          {/* 🗺️ Map - only show when not manual address */}
          {!manualAddress && (
            <Box sx={{ position: "relative", height: 300, borderRadius: 2, overflow: "hidden" }}>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={position}
                zoom={15}
              >
                <Marker position={position} />
                <Circle
                  center={position}
                  radius={circularRadius}
                  options={{
                    fillColor: "#3f51b5",
                    fillOpacity: 0.2,
                    strokeColor: "#3f51b5",
                    strokeOpacity: 1,
                  }}
                />
              </GoogleMap>
            </Box>
          )}

        {/* 📍 Address input - only show when not manual address */}
        {!manualAddress && (
          <textarea
            ref={addressRef}
            type="text"
            readOnly={true}
            value={address}
            placeholder="Address will be detected from map"
           style={{
             width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    marginTop: "10px",
    backgroundColor: "#f4f4f4",
    fontFamily: "Poppins, sans-serif",
    resize: "vertical",      // allow manual resize if needed
    overflowY: "auto",       // show scroll when text exceeds height
    lineHeight: "1.4",
        }}
          />
        )}

        {/* Manual Address toggle */}
    


        {/* Country / State / City dropdowns */}
        {manualAddress && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            {/* Country */}
            <Box>
              <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, letterSpacing: "0.3px", textTransform: "capitalize", textAlign: "left" }}>
               {t("settings.country")} <span style={{ color: "red" }}>*</span>
              </CustomText>
              <FormControl fullWidth size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                      pointerEvents: dropdownLocked ? "none" : "auto",

                    borderRadius: "10px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
                    "&:hover fieldset": { borderColor: "#1976d2" },
                    "&.Mui-focused fieldset": { borderColor: "#1976d2", boxShadow: "0 0 0 2px rgba(25,118,210,0.08)" },
                  },
                }}
              >
   
  <SearchableDropdown
    value={selectedCountry}
    onChange={handleCountryChange}
    options={countriesData.map((c) => c.name)}  
    placeholder="settings.country"
      disabled={dropdownLocked}
  />
  {loadingStates && (
    <CircularProgress
      size={20}
      sx={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
      }}
    />
  )}

              </FormControl>
            </Box>

            {/* State */}
            <Box>
              <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, letterSpacing: "0.3px", textTransform: "capitalize", textAlign: "left" }}>
            {t("settings.province")} <span style={{ color: "red" }}>*</span>
              </CustomText>
              <FormControl fullWidth size="small" disabled={!selectedCountry}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                      pointerEvents: dropdownLocked ? "none" : "auto",

                    borderRadius: "10px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    backgroundColor: selectedCountry ? "#fff" : "#f5f5f5",
                    "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
                    "&:hover fieldset": { borderColor: selectedCountry ? "#1976d2" : "#ccc" },
                    "&.Mui-focused fieldset": { borderColor: selectedCountry ? "#1976d2" : "#ccc" },
                    "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
                  },
                }}
              >
     
  <SearchableDropdown
    value={selectedState}
    onChange={handleStateChange}
    options={statesList.map((s) => s.name)}
    placeholder="settings.province"
    // disabled={!selectedCountry}
      disabled={true}


  />
  {loadingCities && (
    <CircularProgress
      size={20}
      sx={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
      }}
    />
  )}
              </FormControl>
            </Box>

            {/* City */}
            <Box>
              <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, letterSpacing: "0.3px", textTransform: "capitalize", textAlign: "left" }}>
             {t("settings.city")} <span style={{ color: "red" }}>*</span>
              </CustomText>
              <FormControl fullWidth size="small" disabled={!selectedState}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 44,
                      pointerEvents: dropdownLocked ? "none" : "auto",

                    borderRadius: "10px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    backgroundColor: selectedState ? "#fff" : "#f5f5f5",
                    "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
                    "&:hover fieldset": { borderColor: selectedState ? "#1976d2" : "#ccc" },
                    "&.Mui-focused fieldset": { borderColor: selectedState ? "#1976d2" : "#ccc" },
                    "&.Mui-disabled": { backgroundColor: "#f5f5f5" },
                  },
                }}
              >
       
<SearchableDropdown
    value={selectedCity}
    onChange={handleCityChange}
    options={citiesList}
    placeholder="settings.city"
    // disabled={!selectedState}
      disabled={true}

  />

              </FormControl>
            </Box>
          </Box>
        )}

        {manualAddress && (
          <Box sx={{ mt: 2 }}>
            <CustomText customStyle={{ ...textStyles.body5, color: "#6B778C", mb: 0.5, letterSpacing: "0.3px", textTransform: "capitalize", textAlign: "left" }}>
                {t("settings.address")}<span style={{ color: "red" }}>*</span>
            </CustomText>
            <input
              type="text"
                                disabled={true}

               placeholder={t("settings.addressPlaceholder")}
              value={displayAddress}
              onChange={(e) => setDisplayAddress(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "16px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
                fontFamily: "Poppins, sans-serif",

              }}
            />
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 2, gap: 2 }}>
          <CustomButton
            label={t("back")}
            onClick={() => navigate(-1)}
             disabled={isContinueClicked} 
            size="medium"
            sx={{
              fontSize: "16px",
              px: 4,
               backgroundColor: isContinueClicked ? "#f0f0f0" : "white",
    color: isContinueClicked ? "#999" : "black",
    "&:hover": {
      backgroundColor: isContinueClicked ? "#f0f0f0" : "white",
      color: isContinueClicked ? "#999" : "black",
    },
            }}
          />

          <CustomButton
            onClick={handleContinue}
            disabled={saving}
            label={
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                {saving && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                {t("continue")}
              </Box>
            }
            sx={{ py: 1, px: 6 }}
          />
        </Box>
        </BaseCard>

        {/* Full-screen Map */}
        {showFullMap && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 1300,
              p: 2,
            }}
          >
            <IconButton onClick={() => setShowFullMap(false)} sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}>
              <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
            </IconButton>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={position}
              zoom={15}
            >
              <Marker position={position} />
              <Circle
                center={position}
                radius={circularRadius}
                options={{
                  fillColor: "#3f51b5",
                  fillOpacity: 0.2,
                  strokeColor: "#3f51b5",
                  strokeOpacity: 1,
                }}
              />
            </GoogleMap>
          </Box>
        )}

        {/* Permission Modal */}
        <LocationPermissionModal
          open={showPermissionModal}
          onAllow={() => {
            setShowPermissionModal(false);
            handleGeolocate();
          }}
          onCancel={() => setShowPermissionModal(false)}
        />
      </Box>
  );
};

export default Location;
